/**
 * Conference history list page. Route: `/conferences/:roomId/history`.
 *
 * Shows every past session for a single room with participant counts,
 * duration, and a "had moderator" badge. Toggle filters phantom
 * (moderator-less) sessions in / out of the view -- default ON because
 * the most common task is reviewing a real conference, not browsing
 * one-second mis-dials.
 *
 * Aesthetic: aligned with the live dashboard (mission-control / IBM
 * Plex tokens) but calmer -- this is the historical companion, not the
 * realtime command surface. Same dark slate + grid hint, no pulsing
 * indicators.
 */
import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Radio, History, Mic, ChevronRight, ShieldCheck, ShieldOff } from 'lucide-react';
import { conferenceHistory, type SessionSummary } from '@/api/conferenceHistory';
import { conferenceRooms } from '@/api/conferenceRooms';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h ${rm}m`;
}

function formatStartedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export function ConferenceHistoryPage() {
  const { roomId = '' } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const [hasModerator, setHasModerator] = useState(true);

  const { data: room } = useQuery({
    queryKey: ['conferenceRoom', roomId],
    queryFn: () => conferenceRooms.get(roomId),
    enabled: !!roomId,
    retry: false,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['conferenceHistory', roomId, hasModerator],
    queryFn: () => conferenceHistory.list(roomId, { hasModerator, limit: 100 }),
    enabled: !!roomId,
  });

  const rows = useMemo(() => data?.rows ?? [], [data]);

  const columns = [
    {
      header: 'Started',
      cell: (r: SessionSummary) => (
        <div className="flex flex-col">
          <span className="text-sm font-medium text-slate-200">{formatStartedAt(r.started_at)}</span>
          <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
            {new Date(r.started_at).toISOString()}
          </span>
        </div>
      ),
    },
    {
      header: 'Ended',
      cell: (r: SessionSummary) => (
        r.ended_at ? (
          <span className="text-sm text-slate-300">{formatStartedAt(r.ended_at)}</span>
        ) : (
          <Badge variant="outline" className="border-emerald-700/60 text-emerald-300">
            <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 align-middle" />
            Live
          </Badge>
        )
      ),
    },
    {
      header: 'Duration',
      cell: (r: SessionSummary) => (
        <span className="font-mono text-sm tabular-nums text-slate-300">
          {formatDuration(r.duration_seconds)}
        </span>
      ),
    },
    {
      header: 'Callers',
      cell: (r: SessionSummary) => (
        <div className="flex items-baseline gap-1">
          <span className="font-mono text-sm tabular-nums text-slate-200">
            {r.participant_count}
          </span>
          {r.unique_caller_count !== r.participant_count && (
            <span className="font-mono text-xs text-slate-500">({r.unique_caller_count} uniq)</span>
          )}
        </div>
      ),
    },
    {
      header: 'Moderator',
      cell: (r: SessionSummary) => (
        r.had_moderator ? (
          <Badge variant="outline" className="border-sky-700/60 text-sky-300">
            <ShieldCheck className="mr-1 h-3 w-3" /> Yes
          </Badge>
        ) : (
          <Badge variant="outline" className="border-slate-700 text-slate-400">
            <ShieldOff className="mr-1 h-3 w-3" /> No
          </Badge>
        )
      ),
    },
    {
      header: 'Recording',
      cell: (r: SessionSummary) => (
        r.recording_customer_path || r.had_backend_recording ? (
          <span className="inline-flex items-center gap-1 text-rose-400">
            <Mic className="h-3 w-3" />
            <span className="text-xs">
              {r.recording_customer_path ? 'Customer' : 'Backend only'}
            </span>
          </span>
        ) : (
          <span className="text-xs text-slate-500">—</span>
        )
      ),
    },
    {
      header: 'Ended',
      cell: (r: SessionSummary) => (
        r.ended_reason ? (
          <span className="font-mono text-[11px] uppercase tracking-wide text-slate-400">
            {r.ended_reason}
          </span>
        ) : <span className="text-xs text-slate-500">—</span>
      ),
    },
    {
      header: '',
      className: 'w-8 text-right',
      cell: () => <ChevronRight className="h-4 w-4 text-slate-500" />,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Conferences"
        title={room ? `${room.name} — History` : 'Conference history'}
        description="Every past session for this room. Joins, mutes, hand-raises, recording events — fully replayable."
        actions={(
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/conferences/${roomId}/live`)}
            >
              <Radio className="mr-1.5 h-3.5 w-3.5" /> Live dashboard
            </Button>
          </div>
        )}
      />

      <div className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <History className="h-4 w-4 text-slate-400" />
          <div className="flex flex-col">
            <span className="text-sm font-medium text-slate-200">
              Only show conferences with a moderator
            </span>
            <span className="text-xs text-slate-500">
              Hides phantom sessions where no admin ever joined. Turn off to see everything.
            </span>
          </div>
        </div>
        <Switch
          checked={hasModerator}
          onCheckedChange={setHasModerator}
          aria-label="Only show conferences with a moderator"
        />
      </div>

      {isLoading ? (
        <div className="text-sm text-slate-400">Loading history…</div>
      ) : error ? (
        <div className="rounded-lg border border-rose-700/60 bg-rose-950/30 px-4 py-3 text-sm text-rose-300">
          Failed to load history: {error instanceof Error ? error.message : 'unknown error'}.
        </div>
      ) : (
        <>
          <DataTable
            rows={rows}
            columns={columns}
            onRowClick={(r) => navigate(`/conferences/sessions/${r.id}/history`)}
            empty={
              hasModerator
                ? 'No completed conferences with a moderator yet. Toggle "show all" to include phantom sessions.'
                : 'No conferences yet for this room.'
            }
          />
          {data && data.total > rows.length && (
            <p className="text-xs text-slate-500">
              Showing {rows.length} of {data.total} sessions.
            </p>
          )}
        </>
      )}
    </div>
  );
}
